import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { Disclaimer } from 'src/app/shared/models/disclaimer';
import { map } from 'rxjs/operators';
import { BaseService } from './base.service';
import { getParsedError } from './error-handler-factory.service';
import { StringIgnoreCaseComparator } from '../utils/comparators';
import { Moment } from 'moment';
import { ApiVersions } from './api-version';

@Injectable()
export class DisclaimerService extends BaseService {
  protected _disclaimers: BehaviorSubject<Disclaimer[]> = new BehaviorSubject<Disclaimer[]>([]);
  public createdDisclaimer: Subject<Disclaimer> = new Subject<Disclaimer>();
  public updatedDisclaimer: Subject<Disclaimer> = new Subject<Disclaimer>();

  private basePath = 'disclaimers';

  constructor() {
    super();
  }

  public getDisclaimers(): void {
    if (this._disclaimers.getValue().length > 0) {
      return;
    }
    const obs = this.get(this.basePath).pipe(
      map((data) => data.map((disclaimer) => Disclaimer.parse(disclaimer)))
    );

    obs.subscribe((data) => {
      this._disclaimers.next(data.sort(StringIgnoreCaseComparator('name')));
    });
  }

  public get disclaimers(): Observable<Disclaimer[]> {
    return this._disclaimers.asObservable();
  }

  isInUse(disclaimerId: string): Observable<boolean> {
    return this.get(`${this.basePath}/in-use/${disclaimerId}`);
  }

  getDownloadUrl(disclaimerId: string): Observable<any> {
    return this.get(this.getDisclaimerSignedUrl(disclaimerId), false, undefined, true);
  }

  private getDisclaimerSignedUrl(disclaimerId: string): string {
    return `disclaimers/${disclaimerId}/signedUrl`;
  }

  deleteDisclaimer(disclaimer: Disclaimer): void {
    this.delete(`${this.basePath}/${disclaimer.id}`).subscribe(
      () => {
        const disclaimers = this._disclaimers.value;
        const index = disclaimers.findIndex((a) => a.id === disclaimer.id);
        disclaimers.splice(index, 1);
        this._disclaimers.next(disclaimers.sort(StringIgnoreCaseComparator('name')));
        this.openSnackBar(`Successfully deleted ${disclaimer.title}`, true);
      },
      (error) => {
        this.openSnackBar(error, false);
      }
    );
  }

  createDisclaimer(disclaimer: Disclaimer): void {
    const existing = this._disclaimers.value;
    const hasExisting = existing.findIndex(
      (a) =>
        a.title === disclaimer.title &&
        a.activeStartDate.get('date') === disclaimer.activeStartDate.get('date')
    );

    if (hasExisting !== -1) {
      this.openSnackBar('The Title and Active Start Date must be unique', false);
      return;
    }

    this.post(this.basePath, JSON.stringify(disclaimer), null, ApiVersions.v0_1, true).subscribe(
      (res) => {
        const disclaimers = this._disclaimers.value;
        const disclaimerResult = Disclaimer.parse(res);
        disclaimers.push(disclaimerResult);
        this.createdDisclaimer.next(disclaimerResult);
        this._disclaimers.next(disclaimers.sort(StringIgnoreCaseComparator('name')));
      },
      (error) => {
        const parsedMessage = getParsedError(error);

        const errorMessage =
          parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;

        this.openSnackBar(errorMessage, false);
      }
    );
  }

  updateDisclaimer(disclaimer: Disclaimer): void {
    const existing = this._disclaimers.value;
    const hasExisting = existing.findIndex(
      (a) =>
        a.id !== disclaimer.id &&
        a.title === disclaimer.title &&
        a.activeStartDate.get('date') === disclaimer.activeStartDate.get('date')
    );

    if (hasExisting !== -1) {
      this.openSnackBar('The Title and Active Start Date must be unique', false);
      return;
    }

    const body = JSON.stringify(disclaimer);
    const path = this.basePath.concat('/', disclaimer.id);
    this.put(path, body, undefined, undefined, true).subscribe((result) => {
      const disclaimers = this._disclaimers.getValue();
      const updated = Disclaimer.parse(result);
      const index = disclaimers.findIndex((d) => d.id === updated.id);
      disclaimers[index] = updated;
      this.updatedDisclaimer.next(result);
      this._disclaimers.next(disclaimers);
    });
  }

  syncLocal(disclaimer: Disclaimer): void {
    const disclaimers = this._disclaimers.value;
    const index = disclaimers.findIndex((a) => a.id === disclaimer.id);
    disclaimers[index] = disclaimer;
  }

  removeAttachment(disclaimer: Disclaimer): Observable<any> {
    return this.delete(`${this.basePath}/${disclaimer.id}/attachment`);
  }

  getStartOfDay(date: Moment): Moment {
    return date.startOf('day');
  }
}
